import { FormikProps } from 'formik';
import { FC } from 'react';

import { isLessThan36Months } from '@/checkout/templates/Checkout/Forms/CheckoutForm.helpers';
import styles from '@/checkout/templates/Checkout/Forms/CheckoutForm.module.scss';
import {
  FINANCE_QUOTE_RADIO_TYPE,
  FinanceQuoteInfoFormValues,
  residentialStatusOptions,
} from '@/checkout/templates/Checkout/Forms/CheckoutForm.types';
import { yesNoOptions } from '@/constants/constants';
import { FINANCE_RESIDENTIAL_STATUS } from '@/data/FinanceApi/FinanceApi.types';
import { MICROCOPY } from '@/data/microcopy/microcopyDictionary';
import { FormikInputAddressAutocomplete } from '@/general/components/forms/FormikInputAddressAutocomplete/FormikInputAddressAutocomplete';
import { InputRadioFormik } from '@/general/components/forms/Input/InputRadioFormik';
import { InputSelectFormik } from '@/general/components/forms/Input/InputSelectFormik';
import { InputTimeFormik } from '@/general/components/forms/Input/InputTimeFormik';
import { Radio } from '@/general/components/forms/Input/atoms/OptionFieldInput';
import { FamilyResidentialFormValues } from '@/personalised-quote/templates/Forms/PersonalizedQuoteForm.type';
import { getMicrocopyBulk } from '@/stores/microcopyStore';

interface FinanceResidentialStatusFieldsProps {
  formikProps: FormikProps<FinanceQuoteInfoFormValues> | FormikProps<FamilyResidentialFormValues>;
  isBusinessFinanceApplication: boolean;
}

export const FinanceResidentialStatusFields: FC<FinanceResidentialStatusFieldsProps> = ({
  formikProps,
  isBusinessFinanceApplication,
}) => {
  const { values } = formikProps;
  const {
    previousResidentialAddressHeader,
    tenancyAgreementHeader,
    investmentPropertyHeader,
    investmentPropertyMortgageHeader,
    addressPropertyHeader,
    propertyMortgagedHeader,
  } = getMicrocopyBulk({
    previousResidentialAddressHeader: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_PREVIOUS_RESIDENTIAL_ADDRESS_HEADER],
    tenancyAgreementHeader: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_TENANCY_AGREEMENT_HEADER],
    addressPropertyHeader: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_ADDRESS_PROPERTY_HEADER],
    propertyMortgagedHeader: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_IS_MORTGAGED_PROPERTY_HEADER],
    investmentPropertyHeader: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_OWN_INVESTMENT_PROPERTY_HEADER],
    investmentPropertyMortgageHeader: [
      MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_OWN_INVESTMENT_PROPERTY_MORTGAGE_HEADER,
    ],
  });

  return (
    <>
      <div className={styles.residentialStatusContainer}>
        <InputSelectFormik
          name="residentialStatus"
          id="residentialStatus"
          label="Residential status"
          data-testid="residential-status-select"
          options={residentialStatusOptions}
          allowEmpty
        />
        <InputTimeFormik
          label="When did you move in?"
          name="currentAddressStartDate"
          id="currentAddressStartDate"
          variant="month"
          data-testid="current-address-start-date"
          className={styles.moveInDate}
        />
      </div>

      {[FINANCE_RESIDENTIAL_STATUS.HOMEOWNER].includes(values.residentialStatus) && (
        <div className={styles.radioHeader}>
          <p className={styles.fieldTitle}>{propertyMortgagedHeader}</p>
          <div className={styles.radioContainer}>
            <InputRadioFormik
              id="hasMortgageOnCurrentAddress"
              variant="group"
              inputIcon={Radio}
              name="hasMortgageOnCurrentAddress"
              options={yesNoOptions.map((item) => ({
                ...item,
                'data-testid': `has-mortgage-${item.value}`,
              }))}
            />
          </div>
        </div>
      )}

      {!isBusinessFinanceApplication &&
        [FINANCE_RESIDENTIAL_STATUS.RENTING, FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION].includes(
          values.residentialStatus,
        ) && (
          <div className={styles.radioHeader}>
            <p className={styles.fieldTitle}>{tenancyAgreementHeader}</p>
            <div className={styles.radioContainer}>
              <InputRadioFormik
                id="hasTenancyAgreement"
                variant="group"
                inputIcon={Radio}
                name="hasTenancyAgreement"
                options={yesNoOptions}
              />
            </div>
          </div>
        )}

      {[
        FINANCE_RESIDENTIAL_STATUS.RENTING,
        FINANCE_RESIDENTIAL_STATUS.BOARDER,
        FINANCE_RESIDENTIAL_STATUS.LIVING_WITH_PARENTS,
        FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION,
      ].includes(values.residentialStatus) && (
        <div className={styles.radioHeader}>
          <p className={styles.fieldTitle}>{investmentPropertyHeader}</p>
          <div className={styles.radioContainer}>
            <InputRadioFormik
              id="hasInvestmentProperty"
              variant="group"
              inputIcon={Radio}
              name="hasInvestmentProperty"
              options={yesNoOptions.map((item) => ({
                ...item,
                'data-testid': `has-investment-property-${item.value}`,
              }))}
            />
          </div>
        </div>
      )}

      {[
        FINANCE_RESIDENTIAL_STATUS.RENTING,
        FINANCE_RESIDENTIAL_STATUS.BOARDER,
        FINANCE_RESIDENTIAL_STATUS.LIVING_WITH_PARENTS,
        FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION,
      ].includes(values.residentialStatus) &&
        values.hasInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION && (
          <div className={styles.radioHeader}>
            <p className={styles.fieldTitle}>{investmentPropertyMortgageHeader}</p>
            <div className={styles.radioContainer}>
              <InputRadioFormik
                id="hasMortgageOnInvestmentProperty"
                variant="group"
                inputIcon={Radio}
                name="hasMortgageOnInvestmentProperty"
                options={yesNoOptions.map((item) => ({
                  ...item,
                  'data-testid': `has-investment-property-mortgage-${item.value}`,
                }))}
              />
            </div>
          </div>
        )}

      {!isBusinessFinanceApplication &&
        [FINANCE_RESIDENTIAL_STATUS.RENTING, FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION].includes(
          values.residentialStatus,
        ) &&
        values.hasInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION &&
        values.hasMortgageOnInvestmentProperty === FINANCE_QUOTE_RADIO_TYPE.YES_OPTION && (
          <>
            <p className={styles.fieldTitle}>{addressPropertyHeader}</p>
            <FormikInputAddressAutocomplete name="investmentPropertyAddress" {...formikProps} />
          </>
        )}

      {/* Previous address */}
      {isLessThan36Months(formikProps.values.currentAddressStartDate) && (
        <>
          <p className={styles.fieldHeader}>{previousResidentialAddressHeader}</p>
          <FormikInputAddressAutocomplete name="previousResidentialAddress" {...formikProps} />
          <div className={styles.residentialStatusContainer}>
            <InputTimeFormik
              label="When did you move in?"
              name="previousAddressStartDate"
              id="previousAddressStartDate"
              variant="month"
              className={styles.moveInDate}
            />
          </div>
        </>
      )}
    </>
  );
};
